import { useCallback, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";

/** COMPONENT */
import { Box, IconButton, MenuItem, Tooltip, Typography } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckIcon from "@mui/icons-material/Check";
import SubjectIcon from "@mui/icons-material/Subject";
import HeaderCard from "../../component/cardCustom/headerCard";
import TableCustom from "../../component/table/tableCustom";
import TableRowCommon from "../../component/table/TableRowCommon";
import InputTextField from "../../component/input/inputTextField";
import InputDatePicker from "../../component/input/inputDatePicker";
import FilterSelect from "../../component/select/filterSelect";
import Loading from "../../component/loading/loading";
import { notiError } from "../../component/notification/notifications";

/** MODAL */
import ModalDepositRefundDetail from "./modal/ModalDepositRefundDetail";
import ModalDepositRefundReview from "./modal/ModalDepositRefundReview";
import ModalDepositRefundLog from "./modal/ModalDepositRefundLog";

/** API */
import DepositRefundApi, {
  DepositRefundRow,
  DepositRefundSummary,
} from "../../api/deposit-refund";

/** UTILS */
import { colors } from "../../constants/colors";
import { MENUS_FIRST } from "../../constants/menu";
import {
  DEPOSIT_REFUND_FILTER_ALL,
  DEPOSIT_REFUND_STATUS,
  DEPOSIT_REFUND_STATUS_ALL,
  DEPOSIT_REFUND_STATUS_OPTIONS,
  depositRefundStatusBg,
  depositRefundStatusColor,
  depositRefundStatusLabel,
} from "../../constants/depositRefund";
import { dateTimeToView } from "../../utils/date.utils";
import { getApiErrorMessage } from "../../utils/api-error.utils";
import { DASH, amountToView } from "./depositRefund.utils";
import { useMenuPermission } from "./useMenuPermission";
import { useDepositRefundFilter } from "./useDepositRefundFilter";

/** STYLE */
import * as Style from "./depositRefund.style";

const EMPTY_SUMMARY: DepositRefundSummary = {
  all: 0,
  WAITING: 0,
  APPROVE: 0,
  NOT_APPROVE: 0,
  CANCELED: 0,
};

/** ฝั่งเจ้าหน้าที่ — พิจารณาคำร้องขอคืนค่ามัดจำแผงของผู้เช่าทุกราย */
export default function DepositRefund() {
  const permissions = useMenuPermission(MENUS_FIRST.DEPOSIT_REFUND);
  const filter = useDepositRefundFilter("ADMIN");

  const [data, setData] = useState<DepositRefundRow[]>([]);
  const [summary, setSummary] = useState<DepositRefundSummary>(EMPTY_SUMMARY);
  const [rowCount, setRowCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /** modal — ใช้ standalone mode ทุกตัว จึงต้อง conditional render จากตรงนี้ */
  const [detailId, setDetailId] = useState<number | null>(null);
  const [reviewRow, setReviewRow] = useState<DepositRefundRow | null>(null);
  const [logRow, setLogRow] = useState<DepositRefundRow | null>(null);

  const { buildTableParams, buildBaseParams } = filter;

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await DepositRefundApi.findAll("ADMIN", buildTableParams());
      // envelope ของ endpoint ชุดนี้ซ้อน data สองชั้น ไม่ใช่ res.data ตรงๆ แบบ API รุ่นเก่า
      const paginated = res.data?.data;
      setData(paginated?.items || []);
      setRowCount(paginated?.meta?.totalItems || 0);
    } catch (error) {
      setData([]);
      setRowCount(0);
      notiError(getApiErrorMessage(error, "ดึงรายการคำร้องคืนค่ามัดจำไม่สำเร็จ"));
    } finally {
      setIsLoading(false);
    }
  }, [buildTableParams]);

  const loadSummary = useCallback(async () => {
    try {
      const res = await DepositRefundApi.summary("ADMIN", buildBaseParams());
      setSummary(res.data?.data || EMPTY_SUMMARY);
    } catch (error) {
      // ตัวเลขข้าง dropdown ไม่ใช่ข้อมูลหลัก พลาดแล้วให้เงียบ ไม่รบกวนซ้ำกับ error ของตาราง
      setSummary(EMPTY_SUMMARY);
    }
  }, [buildBaseParams]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  /** เรียกหลังพิจารณาสำเร็จ ให้ทั้งตารางและตัวเลขสถานะตรงกัน */
  const reload = () => {
    loadData();
    loadSummary();
  };

  const statusCount = (value: string) =>
    value === DEPOSIT_REFUND_STATUS_ALL
      ? summary.all
      : summary[value as keyof DepositRefundSummary] ?? 0;

  const headCells = [
    { id: "no", disablePadding: false, label: "NO", align: "center" },
    { id: "depositRefundNo", disablePadding: false, label: "เลขที่คำร้อง", align: "center" },
    { id: "leaseAgreementNo", disablePadding: false, label: "เลขที่สัญญา", align: "center" },
    { id: "tenantName", disablePadding: false, label: "ชื่อผู้เช่าแผงค้า", align: "left" },
    { id: "zoneName", disablePadding: false, label: "โซน", align: "left" },
    { id: "stallName", disablePadding: false, label: "แผงค้า", align: "left" },
    { id: "stallNumber", disablePadding: false, label: "เลขแผงค้า", align: "center" },
    { id: "stallTypeName", disablePadding: false, label: "ประเภทแผงค้า", align: "left" },
    { id: "requestAmount", disablePadding: false, label: "ยอดเงินที่ขอคืน", align: "right" },
    { id: "requestDate", disablePadding: false, label: "วันเวลาที่ส่งคำร้อง", align: "center" },
    { id: "reviewedDate", disablePadding: false, label: "วันเวลาที่พิจารณา", align: "center" },
    { id: "reviewerName", disablePadding: false, label: "ผู้พิจารณา", align: "left" },
    { id: "status", disablePadding: false, label: "สถานะ", align: "center" },
    { id: "action", disablePadding: false, label: "Action", align: "center" },
  ];

  const renderAction = (objData: DepositRefundRow) => {
    const isWaiting = objData.status === DEPOSIT_REFUND_STATUS.WAITING;
    const viewDisabled = !permissions.isView;
    // พิจารณาได้เฉพาะคำร้องที่ยังรอ — แถวอื่นให้ปุ่มจางและกดไม่ได้ตามดีไซน์
    const reviewDisabled = !isWaiting || !permissions.isApporve;

    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Tooltip title="รายละเอียดคำร้องคืนค่ามัดจำ">
          <span>
            <IconButton
              disabled={viewDisabled}
              onClick={() => setDetailId(objData.id_deposit_refund)}
              sx={{ color: viewDisabled ? colors.disabledGray : colors.textLightGray }}
            >
              <DescriptionOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={isWaiting ? "พิจารณาคำร้อง" : "คำร้องนี้ถูกพิจารณาแล้ว"}>
          <span>
            <IconButton
              disabled={reviewDisabled}
              onClick={() => setReviewRow(objData)}
              sx={{ color: reviewDisabled ? colors.disabledGray : colors.textLightGray }}
            >
              <CheckIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="ประวัติการพิจารณา">
          <span>
            <IconButton
              disabled={viewDisabled}
              onClick={() => setLogRow(objData)}
              sx={{ color: viewDisabled ? colors.disabledGray : colors.textLightGray }}
            >
              <SubjectIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  const renderData = (objData: DepositRefundRow, no: number) => {
    const runningNo = filter.page * filter.pageLimit - filter.pageLimit + no + 1;

    const objRenderData = {
      key: String(objData.id_deposit_refund),
      id: String(objData.id_deposit_refund),
      obj: objData,
      columns: [
        { option: "TEXT", align: "center", label: runningNo },
        {
          option: "COMPONENT",
          align: "center",
          component: (
            <Style.LinkText onClick={() => setDetailId(objData.id_deposit_refund)}>
              {objData.deposit_refund_no || DASH}
            </Style.LinkText>
          ),
        },
        { option: "TEXT", align: "center", label: objData.lease_agreement_no || DASH },
        { option: "TEXT", align: "left", label: objData.tenant_name || DASH },
        { option: "TEXT", align: "left", label: objData.zone_name || DASH },
        { option: "TEXT", align: "left", label: objData.stall_name || DASH },
        { option: "TEXT", align: "center", label: objData.stall_number || DASH },
        { option: "TEXT", align: "left", label: objData.stall_type_name || DASH },
        {
          option: "TEXT",
          align: "right",
          // request_amount เป็น string (DECIMAL) ต้อง Number() ก่อน format
          // ไม่ใช้ option PRICE เพราะตัวนั้นเรียก numberFormat แบบ default ได้ทศนิยม 2 ตำแหน่ง
          label: amountToView(objData.request_amount),
        },
        // dateTimeToView(undefined) format วันนี้เงียบๆ ต้อง guard ก่อนเสมอ
        {
          option: "TEXT",
          align: "center",
          label: objData.request_date ? dateTimeToView(objData.request_date) : DASH,
        },
        {
          option: "TEXT",
          align: "center",
          label: objData.reviewed_date ? dateTimeToView(objData.reviewed_date) : DASH,
        },
        { option: "TEXT", align: "left", label: objData.reviewed_name || DASH },
        {
          option: "COMPONENT",
          align: "center",
          component: (
            <Style.StatusChip
              chipColor={depositRefundStatusColor(objData.status)}
              chipBg={depositRefundStatusBg(objData.status)}
            >
              {depositRefundStatusLabel(objData.status)}
            </Style.StatusChip>
          ),
        },
        { option: "COMPONENT", align: "center", component: renderAction(objData) },
      ],
    };

    return <TableRowCommon {...objRenderData} />;
  };

  return (
    <>
      {isLoading && <Loading show type="softLoading" />}
      <HeaderCard text="คืนค่ามัดจำแผง" />
      <Style.MainContainer>
        <Row className="align-items-end">
          <Col lg={2} md={6} xs={12} className="my-1">
            <InputTextField
              value={filter.searchInput}
              placeholder="ค้นหาเลขที่คำร้อง / ชื่อผู้เช่าแผงค้า / เลขที่สัญญา"
              onchange={(e: any) => filter.setSearchInput(e.target.value)}
            />
          </Col>
          <Col lg={2} md={6} xs={12} className="my-1">
            <FilterSelect
              labelId="deposit-refund-zone"
              selectId="deposit-refund-zone"
              value={filter.zoneId}
              onchange={(event: any) => filter.handleChangeZone(event.target.value)}
              renderValue={() => `โซน : ${filter.zoneLabel}`}
              options={[
                <MenuItem key="zone-all" value={DEPOSIT_REFUND_FILTER_ALL}>
                  ทั้งหมด
                </MenuItem>,
                ...filter.zones.map((zone) => (
                  <MenuItem key={zone.zoneId} value={zone.zoneId}>
                    {zone.zone_name || DASH}
                  </MenuItem>
                )),
              ]}
            />
          </Col>
          <Col lg={2} md={6} xs={12} className="my-1">
            <FilterSelect
              labelId="deposit-refund-stall"
              selectId="deposit-refund-stall"
              value={filter.stallId}
              onchange={(event: any) => filter.handleChangeStall(event.target.value)}
              renderValue={() => `แผงค้า : ${filter.stallLabel}`}
              options={[
                <MenuItem key="stall-all" value={DEPOSIT_REFUND_FILTER_ALL}>
                  ทั้งหมด
                </MenuItem>,
                ...filter.stallOptions.map((stall) => (
                  <MenuItem key={stall.stallId} value={stall.stallId}>
                    {filter.stallOptionLabel(stall)}
                  </MenuItem>
                )),
              ]}
            />
          </Col>
          <Col lg={2} md={6} xs={12} className="my-1">
            <FilterSelect
              labelId="deposit-refund-status"
              selectId="deposit-refund-status"
              value={filter.status}
              onchange={(event: any) => filter.handleChangeStatus(event.target.value)}
              renderValue={() => `สถานะ : ${depositRefundStatusLabel(filter.status)}`}
              options={DEPOSIT_REFUND_STATUS_OPTIONS.map((option: string) => (
                <MenuItem key={option} value={option}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      gap: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: depositRefundStatusColor(option),
                        }}
                      />
                      {depositRefundStatusLabel(option)}
                    </Box>
                    <Typography component="span" sx={{ fontSize: 14, color: colors.textLightGray }}>
                      {statusCount(option)}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            />
          </Col>
          <Col lg={2} md={6} xs={12} className="my-1">
            <InputDatePicker
              value={filter.dateStart || null}
              onChange={filter.handleChangeDateStart}
              onClear={() => filter.handleChangeDateStart(null)}
              allowClear
              label="วันที่เริ่มต้น"
              placeholder="เลือกวันที่"
              disableEndDate={filter.dateEnd || undefined}
            />
          </Col>
          <Col lg={2} md={6} xs={12} className="my-1">
            <InputDatePicker
              value={filter.dateEnd || null}
              onChange={filter.handleChangeDateEnd}
              onClear={() => filter.handleChangeDateEnd(null)}
              allowClear
              label="วันที่สิ้นสุด"
              placeholder="เลือกวันที่"
              disableStartDate={filter.dateStart || undefined}
            />
          </Col>
        </Row>

        <Style.TableContainer>
          <TableCustom
            headCells={headCells}
            customScroll
            page={filter.page}
            pageLimit={filter.pageLimit}
            sortType="ASC"
            sortBy=""
            rowCount={rowCount}
            rowsData={data.map((row, index) => renderData(row, index))}
            // API ชุดนี้ยังไม่รองรับการเรียงลำดับ หัวตารางจึงไม่มีปุ่ม sort
            onSort={() => {}}
            setPageLimit={filter.handleChangePageLimit}
            setPage={(value: number) => filter.setPage(value)}
            textEmptyData="ไม่พบคำร้องคืนค่ามัดจำ"
            tableMinWidth={1900}
          />
        </Style.TableContainer>
      </Style.MainContainer>

      {detailId !== null && (
        <ModalDepositRefundDetail
          id={detailId}
          side="ADMIN"
          onClose={() => setDetailId(null)}
        />
      )}

      {reviewRow && (
        <ModalDepositRefundReview
          row={reviewRow}
          onClose={() => setReviewRow(null)}
          onSuccess={() => {
            setReviewRow(null);
            reload();
          }}
        />
      )}

      {logRow && (
        <ModalDepositRefundLog
          id={logRow.id_deposit_refund}
          side="ADMIN"
          subTitle={logRow.deposit_refund_no}
          receiptScope={{ userId: logRow.userId, stallId: logRow.stallId }}
          onClose={() => setLogRow(null)}
        />
      )}
    </>
  );
}
